import { DocumentGenerationGetResponse, DocumentSearchResponse } from '@lio/api-types';
import { ArrayItem } from '@utils/commonTypes';

export type Document = ArrayItem<DocumentSearchResponse['items']>;
export type DocumentStatusTypes = DocumentGenerationGetResponse['status'];
export const DocumentStatus: Record<DocumentStatusTypes, DocumentStatusTypes> = {
  Pending: 'Pending',
  InProgress: 'InProgress',
  Failed: 'Failed',
  Completed: 'Completed',
};

export const DocumentType = {
  Application: {
    id: 'clve5xenj0001h8v5bnfi4tud',
    name: 'Application',
  },
  CertificateOfInsurance: {
    name: 'Certificate Of Insurance',
    id: 'clzbqmnlr0000356scgu8km38',
  },
  Other: {
    name: 'Additional Document',
    id: 'clczvjpv40000rcv58otxb2q7',
  },
  QuoteProposal: {
    id: 'clb76qt420000okfeegoo1qzo',
    name: 'Quote Proposal Package',
  },
  Policy: { id: 'clb76qt420001okfe2n5t4qup', name: 'Policy' },
  Endorsement: { id: 'clb76qt420002okfe15ruhvh3', name: 'Endorsement' },
  Cancellation: { id: 'clb76qt420005okfeh56e3scw', name: 'Cancellation' },
  Rewrite: { id: 'clb76qt430007okfe7zo0267i', name: 'Rewrite' },
  Binder: { id: 'clqpo6bq80000e1v5eoqc7f0h', name: 'Binder' },
  ConfirmationOfCoverage: { id: 'clqpo6bq80000e1v5eoqc7f0h', name: 'Confirmation of Coverage' }, // Same document type as Binder but different label.
  SurplusLinesFilingConfirmation: {
    id: 'cma1lr3d00000p4va13s7hypf',
    name: 'Surplus Lines Filing Confirmation',
  },
  LossHistoryReport: {
    id: 'cmhyvlfnc00013j6zzj1i7aff',
    name: 'Loss History Report',
  },
  NoticeOfIntentToCancel: {
    id: 'clb76qt420004okfeav9t3q86',
    name: 'Notice Of Intent To Cancel',
  },
  NoticeOfCancellation: {
    id: 'cmmw4hjjd00093572kj35rvim',
    name: 'Notice Of Cancellation',
  },
  NoticeOfConditionalRenewal: {
    id: 'cmmw4hjjd00053572pg01vcic',
    name: 'Notice Of Conditional Renewal',
  },
  NoticeOfNonrenewal: {
    id: 'cmmw4hjjd00063572or5i3zxg',
    name: 'Notice Of Nonrenewal',
  },
  NoticeOfRescissionOfPendingNonrenewal: {
    id: 'cmmw4hjjd000735721xkexxl4',
    name: 'Notice Of Rescission Of Pending Nonrenewal',
  },
  NoticeOfRescissionOfPendingCancellation: {
    id: 'cmmw4hjjd00083572tnldkwxk',
    name: 'Notice Of Rescission Of Pending Cancellation',
  },
  FraudStatementDisclosure: {
    id: 'cme2wkqtf000d3j6xldl6vyli',
    name: 'Fraud Statement Disclosure',
  },
};

export const PackageTypeId = {
  QuoteProposal: 'clb29ayap0000oife567s2b4z',
  NewBusiness: 'clb29ayaq0001oife41fa95bw',
  Application: 'clve5xeni0000h8v575dh4alm',
  CertificateOfInsurance: 'clzbqmnlr0001356shvksz9l2',
  SurplusLinesFilingConfirmation: 'cma1lom750000mkva6krm70dn',
  LossHistoryReport: 'cmhyvlfnc00003j6zw4kshktp',
  NoticeOfConditionalRenewal: 'cmmw4hjjd0000357295376ne2',
  NoticeOfNonrenewal: 'cmmw4hjjd00013572awrqc2wv',
  NoticeOfRescissionOfPendingNonrenewal: 'cmmw4hjjd00023572ge5frn6q',
  NoticeOfRescissionOfPendingCancellation: 'cmmw4hjjd00033572p0w2fxxx',
  NoticeOfCancellation: 'cmmw4hjjd00043572k5o7d9u6',

  // hmmm
  Policy: 'clb29ayaq0001oife41fa95bw',
  Endorsement: 'clb29ayaq0002oife7ft0hvax',
  RenewalQuote: 'clb29ayaq0003oifed81o02n4',
  NoticeOfIntentToCancel: 'clb29ayaq0004oife8kuq3yew',
  Cancelation: 'clb29ayaq0005oife1b8dd5rt',
  RescindCancelation: 'clb29ayaq0006oifeb233dr4y',
  Rewrite: 'clb29ayaq0007oife4bsbc1a9',
  Reinstatement: 'cldukyikl0000b3v54mla6bs2',
  Binder: 'clqpo34od00005vv53stvblga',
  ApplicationExport: 'clve5xeni0000h8v575dh4alm',
  FraudStatement: 'cme2wkqtf000c3j6x7gj6ntz5',
};

export const DocumentByTypeId: {
  [key: string]: { label: string; packageTypeId: string };
} = {
  clzbqmnlr0000356scgu8km38: {
    label: 'Certificate Of Insurance',
    packageTypeId: 'clzbqmnlr0001356shvksz9l2',
  },
  clb76qt420001okfe2n5t4qup: {
    label: 'New Business',
    packageTypeId: 'clb29ayaq0001oife41fa95bw',
  },
  clb76qt420002okfe15ruhvh3: {
    label: 'Endorsement',
    packageTypeId: 'clb29ayaq0002oife7ft0hvax',
  },
  clb76qt420004okfeav9t3q86: {
    label: 'Notice Of Intent To Cancel',
    packageTypeId: 'clb29ayaq0004oife8kuq3yew',
  },
  clb76qt420005okfeh56e3scw: {
    label: 'Cancellation',
    packageTypeId: 'clb29ayaq0005oife1b8dd5rt',
  },
  clb76qt430006okfeh869c0ld: {
    label: 'Rescind Cancellation',
    packageTypeId: 'clb29ayaq0006oifeb233dr4y',
  },
  clb76qt430007okfe7zo0267i: {
    label: 'Rewrite',
    packageTypeId: 'clb29ayaq0007oife4bsbc1a9',
  },
  cldukyikl0001b3v5fe0oecy2: {
    label: 'Reinstatement',
    packageTypeId: 'cldukyikl0000b3v54mla6bs2',
  },
  clqpo6bq80000e1v5eoqc7f0h: {
    label: 'Binder',
    packageTypeId: 'clqpo34od00005vv53stvblga',
  },
  cma1lr3d00000p4va13s7hypf: {
    label: 'Surplus Lines Filing Confirmation',
    packageTypeId: 'cma1lom750000mkva6krm70dn',
  },
  cme2wkqtf000d3j6xldl6vyli: {
    label: 'Fraud Statement Disclosure',
    packageTypeId: 'cme2wkqtf000c3j6x7gj6ntz5',
  },
  cmhyvlfnc00013j6zzj1i7aff: {
    label: 'Loss History Report',
    packageTypeId: PackageTypeId.LossHistoryReport,
  },
  cmmw4hjjd00053572pg01vcic: {
    label: 'Notice Of Conditional Renewal',
    packageTypeId: PackageTypeId.NoticeOfConditionalRenewal,
  },
  cmmw4hjjd00063572or5i3zxg: {
    label: 'Notice Of Nonrenewal',
    packageTypeId: PackageTypeId.NoticeOfNonrenewal,
  },
  cmmw4hjjd000735721xkexxl4: {
    label: 'Notice Of Rescission Of Pending Nonrenewal',
    packageTypeId: PackageTypeId.NoticeOfRescissionOfPendingNonrenewal,
  },
  cmmw4hjjd00083572tnldkwxk: {
    label: 'Notice Of Rescission Of Pending Cancellation',
    packageTypeId: PackageTypeId.NoticeOfRescissionOfPendingCancellation,
  },
  cmmw4hjjd00093572kj35rvim: {
    label: 'Notice Of Cancellation',
    packageTypeId: PackageTypeId.NoticeOfCancellation,
  },
} as const;
